import * as automation from '../actions/automation';
import {
  AutomationCampaignDetails,
  AutomationDetails,
  AutomationDisplay
} from '../models/discover/automation-template.model';
import { Customizations } from '../models/engage/template-details.model';
import { clone } from 'ramda';
import { DiscoverConstants } from 'spotlight-ui/app/discover/discover.constants';
import { CampaignType } from 'spotlight-ui/app/models/engage/campaign-details.model';

export interface State {
  automationID: string;
  mode: string;
  selectedAutomationError: any;
  selectedAutomationLoading: boolean;
  display: AutomationDisplay;
  customizations: Customizations;
  automationCampaignDetails: AutomationCampaignDetails;
  templateID: string;
  goal?: string;
  automationDetails: AutomationDetails;
  customerCount: number;
  templateLoading: boolean;
  templateError: any;
  customerIDs: string[];
  type:CampaignType;
}

export const initialState: State = {
  automationID: '',
  mode: DiscoverConstants.create,
  selectedAutomationLoading: false,
  selectedAutomationError: undefined,
  display: {} as AutomationDisplay,
  customizations: {} as Customizations,
  automationCampaignDetails: {} as AutomationCampaignDetails,
  automationDetails: {
    durationDays: 30,
    launchTime: '',
    isAutomated: false,
    canAutomate: false
  } as AutomationDetails,
  customerCount: 0,
  templateID: '',
  templateLoading: false,
  templateError: undefined,
  customerIDs: [],
  type: CampaignType.Reward
}

export function reducer(
  state = initialState,
  action: automation.Actions,
) {
  switch (action.type) {
    case automation.ActionTypes.SELECT_AUTOMATION: {
      return {
        ...state,
        templateLoading: true,
        templateError: undefined,
        automationID: action.payload.fieldId,
        mode: action.payload.engagementMode,
        type: action.payload.engagementType,
        customerIDs: action.payload.customers,
        display: {} as AutomationDisplay,
        customizations: {} as Customizations,
        automationCampaignDetails: {} as AutomationCampaignDetails,
        automationDetails: {
          launchTime: '',
          isAutomated: action.payload.automate,
          canAutomate: action.payload.engagementType != CampaignType.Notification && action.payload?.count > 0
        },
        customerCount: action.payload?.count ?? 0,
        goal: action.payload.goal
      }
    }
    case automation.ActionTypes.SELECT_AUTOMATION_SUCCESS: {
      let templatesDetails = action.payload.campaignTemplateDetails.filter(template => template.campaignType === state.type)
      let selectedCampaign = action.payload.campaignTemplateID ?
        templatesDetails.find(template => template.id === action.payload.campaignTemplateID) : templatesDetails[0];
      let selectedTheme = selectedCampaign?.notificationLayouts[0].themes[0];
      if (selectedCampaign && selectedTheme) {
        let isExistingAutomation = action.payload.automationID != undefined;
        let customizations = action.payload.customizations ?? action.payload.emailCustomizations ;
        let namePrefix = state.automationDetails.isAutomated ? 'Automated ' : '';
        return {
          ...state,
          automationID: isExistingAutomation ? action.payload.automationID : '',
          templateID: isExistingAutomation ? action.payload.automationTemplateID : action.payload.id,
          display: {
            ...action.payload.display
          },
          automationCampaignDetails: {
            durationDays: action.payload.reward?.durationDays ?? 30,
            includeActiveLocationRewardCustomers: action.payload.reward?.includeActiveLocationRewardCustomers ?? true,
            appMessage: action.payload.reward?.appMessage ?? selectedTheme.appMessage,
            campaignTemplates: templatesDetails,
            selectedCampaignTemplateID: selectedCampaign.id,
            title: selectedCampaign.title
          },
          automationDetails: {
            launchTime: action.payload.launchTime,
            isAutomated: isExistingAutomation || state.automationDetails.isAutomated,
            canAutomate: state.automationDetails.canAutomate
          },
          customizations: {
            name:  isExistingAutomation ? action.payload.name : (namePrefix + action.payload.display.title),
            subject: customizations?.subject ?? selectedTheme.subject,
            body: customizations?.body ?? customizations?.emailBody ?? selectedTheme.emailBody,
            themeID: selectedTheme.id,
            heroImage: customizations?.heroImage ?? selectedTheme.heroImage ?? selectedCampaign.heroImages[0],
          },
          templateLoading: false
        }
      } else {
        return {
          ...state,
          templateError: 'Something about being malformed',
          templateLoading: false
        }
      }
    }
    case automation.ActionTypes.SELECT_AUTOMATION_FAILURE: {
      return {
        ...state,
        templateError: action.payload,
        templateLoading: false
      }
    }
    case automation.ActionTypes.UPDATE_AUTOMATION_CUSTOMIZATION: {
      const update = action.payload;
      return {
        ...state,
        customizations: {
          ...state.customizations,
          ...update
        }
      }
    }
    case automation.ActionTypes.UPDATE_AUTOMATION_REWARD: {
      const update = action.payload;
      let customizations = clone(state.customizations)
      let title = ''

      if ( update.selectedCampaignTemplateID ) {
        let selectedCampaign = state.automationCampaignDetails.campaignTemplates
          .find(template => template.id === update.selectedCampaignTemplateID );
        let selectedTheme = selectedCampaign?.notificationLayouts[0].themes[0];
        if (selectedCampaign && selectedTheme ) {
          title = selectedCampaign?.title;
          customizations = {
            ...customizations,
            subject: selectedTheme.subject,
            body: selectedTheme.emailBody,
            themeID: selectedTheme.id,
            heroImage: selectedTheme.heroImage ?? selectedCampaign.heroImages[0],
          }
        }
      }
      return {
        ...state,
        customizations,
        automationCampaignDetails: {
          ...state.automationCampaignDetails,
          title,
          ...update
        }
      }
    }
    case automation.ActionTypes.UPDATE_IS_AUTOMATED: {
      const update = action.payload;
      return {
        ...state,
        customizations: {
          ...state.customizations,
          name: update ? 'Automated ' + state.customizations.name: state.customizations.name.replace('Automated ', '')
        },
        automationDetails: {
          ...state.automationDetails,
          isAutomated: update
        }
      }
    }
    case automation.ActionTypes.UPDATE_AUTOMATION_AUDIENCE: {
      return {
        ...state,
        customerCount: action.payload.count,
        customerIDs: action.payload.customers,
        automationDetails: {
          ...state.automationDetails,
          canAutomate: state.type != CampaignType.Notification && action.payload.count > 0
        }
      }
    }
    case automation.ActionTypes.CREATE_AUTOMATION: {
      return {
        ...state,
        selectedAutomationLoading: true
      }
    }
    case automation.ActionTypes.UPDATE_AUTOMATION_COMPLETE:
    case automation.ActionTypes.CREATE_AUTOMATION_COMPLETE: {
      return {
        ...state,
        selectedAutomationLoading: false,
        automationID: action.payload.automationID ?? '',
        selectedAutomationError: action.payload.error ?? undefined
      }
    }

    default: {
      return state;
    }
  }
}
export const getSelectedAutomation = (state: State) => ({
  automationID: state?.automationID,
  display: state?.display,
  automationCampaignDetails: state?.automationCampaignDetails,
  templateID: state?.templateID,
  goal: state?.goal,
  customizations: state?.customizations,
  automationDetails: state?.automationDetails,
  customerCount: state?.customerCount,
  loading: state?.templateLoading,
  type: state?.type,
  customerIDs: state?.customerIDs
})

export const getMode = (state: State) => state?.mode;

export const getSelectedAutomationCreationDetails = (state: State) => ({
  automationID: state?.automationID,
  selectedAutomationLoading: state?.selectedAutomationLoading,
  selectedAutomationError: state?.selectedAutomationError
})

export const getTemplateLoading = (state: State) => state?.templateLoading;

export const getTemplateError = (state: State) => state?.templateError;
